import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiService, type User } from '@/services/api'
import toast from 'react-hot-toast'

interface LoginForm {
  email: string
  password: string
}

interface RegisterForm {
  name: string
  email: string
  password: string
}

interface AuthState {
  user: User | null
  isLoading: boolean
  isInitialized: boolean
  login: (data: LoginForm) => Promise<void>
  register: (data: RegisterForm) => Promise<void>
  logout: () => void
  setUser: (user: User | null) => void
  initialize: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isLoading: false,
      isInitialized: false,

      // Initialize auth state
      initialize: async () => {
        try {
          set({ isLoading: true })
          
          // Check if we have a token
          const token = localStorage.getItem('auth_token')
          if (token) {
            try {
              const user = await apiService.getCurrentUser()
              set({ user, isInitialized: true, isLoading: false })
              return
            } catch (error) {
              // Token is invalid, clear it
              apiService.clearToken()
            }
          }
          
          set({ isInitialized: true, isLoading: false })
        } catch (error) {
          console.error('Auth initialization failed:', error)
          set({ isLoading: false, isInitialized: true })
        }
      },

      // Login (Demo Mode)
      login: async (data: LoginForm) => {
        try {
          set({ isLoading: true })

          // Demo mode - simulate login
          await new Promise(resolve => setTimeout(resolve, 1000))

          const mockUser = {
            id: 'demo-user-' + Date.now(),
            name: data.email.split('@')[0],
            email: data.email,
            avatar_url: undefined,
            created_at: new Date().toISOString()
          }

          set({
            user: mockUser,
            isLoading: false
          })

          toast.success('Welcome to Ping Parking Demo!')

        } catch (error: any) {
          set({ isLoading: false })
          toast.error('Demo login failed')
          throw error
        }
      },

      // Register (Demo Mode)
      register: async (data: RegisterForm) => {
        try {
          set({ isLoading: true })

          // Demo mode - simulate registration
          await new Promise(resolve => setTimeout(resolve, 1000))

          const mockUser = {
            id: 'demo-user-' + Date.now(),
            name: data.name,
            email: data.email,
            avatar_url: undefined,
            created_at: new Date().toISOString()
          }

          set({
            user: mockUser,
            isLoading: false
          })

          toast.success('Demo account created! Welcome to Ping Parking!')

        } catch (error: any) {
          set({ isLoading: false })
          toast.error('Demo registration failed')
          throw error
        }
      },

      // Logout
      logout: () => {
        apiService.clearToken()
        set({ 
          user: null, 
          isLoading: false 
        })
        toast.success('Logged out successfully')
      },

      // Set user
      setUser: (user: User | null) => {
        set({ user })
      }
    }),
    {
      name: 'simple-auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        isInitialized: state.isInitialized 
      }),
    }
  )
)
